import * as React from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import DialogContent from '@material-ui/core/DialogContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import { useMediaQuery } from '@material-ui/core';
import { useStaticQuery, graphql } from 'gatsby';
import FacebookIcon from '@material-ui/icons/Facebook';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import TwitterIcon from '@material-ui/icons/Twitter';
import MailOutline from '@material-ui/icons/MailOutline';
import InsertLink from '@material-ui/icons/InsertLink';
import { LocationLanguageContextType } from '../typings';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { objectToGetParams, shareTemplate, openShareLink } from '../utils/shareTemplates';
import { Languages } from '../utils/getAllSupportedLanguages';

interface ShareDialogProps {
  open: boolean;
  title: string;
  onDialogClose: (value?: any) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogPaper: {
      minWidth: '480px'
    },
    dialogClose: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    dialogFormContainer: {
      display: 'flex',
      padding: '1rem',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    dialogTitle: {
      textAlign: 'center',
      paddingBottom: '0.75em',
      '& p, h6': {
        paddingBottom: '0.5em'
      },
      '& h6': {
        fontWeight: '600'
      }
    },
    shareList: {
      display: 'flex',
      flexDirection: 'row',
      padding: 0
    },
    buttonList: {
      flexDirection: 'column',
      marginBottom: '1rem',
      padding: '0px 5px !important',
      '&:hover, &:hover $listItemText, &:hover $listItemIcon': {
        color: '#2864ac',
        backgroundColor: 'transparent'
      }
    },
    listItemText: {
      textAlign: 'center',
      padding: '0.5rem 0',
      color: 'rgba(0, 0, 0, 0.87)'
    },
    listItemIcon: {
      margin: 'auto',
      width: '36px',
      height: '36px'
    }
  })
);

const ShareDialog: React.FC<ShareDialogProps> = ({ open, title, onDialogClose }) => {
  const data = useStaticQuery(
    graphql`
      query ShareDialogQuery {
        allStrapiShareDialog {
          nodes {
            ShareTitle
            CopyLinkLabel
            CopiedSuccess
            CopiedError
            EmailLabel
            FacebookLabel
            LinkedInLabel
            TwitterLabel
            locale
          }
        }
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `
  );

  const classes = useStyles();
  const [copiedMessage, setCopiedMessage] = React.useState('');

  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? Languages.English;
  const shareDialogContent = data.allStrapiShareDialog.nodes.find((x: any) => x.locale === language);

  const siteURL = data.site.siteMetadata.siteUrl;
  const currentURL = typeof window !== 'undefined' ? siteURL + window.location.pathname : siteURL;

  const handleShareToSocial = (media: string) => {
    let shareParameter = '';
    if (media === 'facebook') {
      shareParameter = objectToGetParams({ u: currentURL, quote: title });
    }
    if (media === 'linkedin') {
      shareParameter = objectToGetParams({ mini: 'true', url: currentURL, title, summary: '', source: '' });
    }
    if (media === 'twitter') {
      shareParameter = objectToGetParams({ text: title, u: currentURL, via: '' });
    }
    if (media === 'email') {
      const subject = `This template might be useful to you!`;
      const body = `Hi! This template ${title} might help in your work processes! \r\nTemplateLink: ${currentURL}`;
      shareParameter = objectToGetParams({ subject, body });
    }
    const shareLink = shareTemplate(media, shareParameter);
    openShareLink(media, shareLink);
  };

  const handleCopyToClipboard = () => {
    // add campaign parameters to track URL in GA
    const copyURL = `${currentURL}?utm_source=template-library&utm_medium=share_link&utm_campaign=share_template`;

    navigator.clipboard.writeText(copyURL).then(
      () => {
        setCopiedMessage(shareDialogContent.CopiedSuccess);
      },
      () => {
        setCopiedMessage(shareDialogContent.CopiedError); // failed if no permission to write to the clipboard
      }
    );
  };

  const handleDialogClose = () => {
    setCopiedMessage('');
    onDialogClose();
  };

  const matchesMobile = useMediaQuery('(max-width: 600px)');

  if (shareDialogContent && !matchesMobile) {
    return (
      <Dialog
        fullScreen={!!matchesMobile}
        maxWidth='sm'
        onClose={handleDialogClose}
        open={open}
        PaperProps={{ classes: { root: classes.dialogPaper } }}
      >
        <div className={classes.dialogClose}>
          <IconButton aria-label='close' onClick={handleDialogClose} className='close-icon-button'>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent style={{ padding: '0px' }}>
          <div className={classes.dialogTitle}>
            <Typography variant='h6'>{shareDialogContent.ShareTitle}</Typography>
          </div>
          <div className={classes.dialogFormContainer}>
            <List className={classes.shareList}>
              <ListItem button onClick={() => handleShareToSocial('email')} className={classes.buttonList}>
                <ListItemIcon>
                  <MailOutline className={classes.listItemIcon} />
                </ListItemIcon>
                <Typography variant='body2' className={classes.listItemText}>
                  {shareDialogContent.EmailLabel}
                </Typography>
              </ListItem>
              <ListItem button onClick={() => handleShareToSocial('linkedin')} className={classes.buttonList}>
                <ListItemIcon>
                  <LinkedInIcon className={classes.listItemIcon} />
                </ListItemIcon>
                <Typography variant='body2' className={classes.listItemText}>
                  {shareDialogContent.LinkedInLabel}
                </Typography>
              </ListItem>
              <ListItem button onClick={() => handleShareToSocial('facebook')} className={classes.buttonList}>
                <ListItemIcon>
                  <FacebookIcon className={classes.listItemIcon} />
                </ListItemIcon>
                <Typography variant='body2' className={classes.listItemText}>
                  {shareDialogContent.FacebookLabel}
                </Typography>
              </ListItem>
              <ListItem button onClick={() => handleShareToSocial('twitter')} className={classes.buttonList}>
                <ListItemIcon>
                  <TwitterIcon className={classes.listItemIcon} />
                </ListItemIcon>
                <Typography variant='body2' className={classes.listItemText}>
                  {shareDialogContent.TwitterLabel}
                </Typography>
              </ListItem>
              <ListItem button onClick={handleCopyToClipboard} className={classes.buttonList}>
                <ListItemIcon>
                  <InsertLink className={classes.listItemIcon} />
                </ListItemIcon>
                <Typography variant='body2' className={classes.listItemText}>
                  {copiedMessage === '' ? shareDialogContent.CopyLinkLabel : copiedMessage}
                </Typography>
              </ListItem>
            </List>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return null;
};

export default ShareDialog;
